// Typed auth domain error built from ApiError (contract 06 §5). Branches ONLY on the backend
// machine code / HTTP kind / structured fields — never on English message substrings. The
// flows/composables (later waves) surface `i18nKey`; the form binds `fields`.
import { ApiError } from '@/shared/http'

export type AuthErrorCode =
  | 'invalidCredentials' // 400 INVALID_CREDENTIALS on /auth/login
  | 'emailExists' // signup: email taken
  | 'mfaInvalidCode' // verify-mfa rejected
  | 'mfaExpired' // temp_token expired / no attempts
  | 'mfaLocked' // server-authored attempt lockout
  | 'mfaResendThrottled' // server-authored resend cooldown
  | 'mfaTooManyCodes' // coarse per-user code budget; `details.retry_after` when the server names one
  | 'totpAlreadyEnabled' // setup/confirm: an authenticator app is already confirmed
  | 'totpSetupRequired' // confirm: no pending setup, or its mailed code expired / ran out of tries
  | 'totpInvalidCode' // confirm: the authenticator app's code is wrong
  | 'totpEmailCodeInvalid' // confirm: the mailed code is wrong; `details.attempts_remaining`
  | 'handoffInvalid' // OAuth handoff expired, replayed, or unknown
  | 'resetInvalid' // reset handoff/token expired, replayed, or unknown
  | 'weakPassword' // 422 password rule from server
  | 'invalidOldPassword' // 400 on the settings password change: current password is wrong
  | 'inviteExpired' // team-join / invite link consumed
  | 'unknown'

/** Backend machine code → AuthErrorCode. Unlisted codes fall through to `unknown`. */
const CODE_MAP: Record<string, AuthErrorCode> = {
  INVALID_CREDENTIALS: 'invalidCredentials',
  EMAIL_EXISTS: 'emailExists',
  WEAK_PASSWORD: 'weakPassword',
  INVALID_OLD_PASSWORD: 'invalidOldPassword',
  MFA_INVALID_CODE: 'mfaInvalidCode',
  MFA_EXPIRED: 'mfaExpired',
  MFA_LOCKED: 'mfaLocked',
  MFA_RESEND_THROTTLED: 'mfaResendThrottled',
  MFA_TOO_MANY_CODES: 'mfaTooManyCodes',
  TOTP_ALREADY_ENABLED: 'totpAlreadyEnabled',
  TOTP_SETUP_REQUIRED: 'totpSetupRequired',
  TOTP_INVALID_CODE: 'totpInvalidCode',
  TOTP_EMAIL_CODE_INVALID: 'totpEmailCodeInvalid',
  RESET_HANDOFF_INVALID: 'resetInvalid',
  RESET_HANDOFF_EXPIRED: 'resetInvalid',
  RESET_HANDOFF_REPLAYED: 'resetInvalid',
  RESET_TOKEN_INVALID: 'resetInvalid',
  RESET_TOKEN_EXPIRED: 'resetInvalid',
  HANDOFF_INVALID: 'handoffInvalid',
  HANDOFF_EXPIRED: 'handoffInvalid',
  HANDOFF_REPLAYED: 'handoffInvalid',
  INVITE_EXPIRED: 'inviteExpired',
}

/** Display i18n key per code. `unknown` reuses the ApiError's own key (the errors.http / errors.api namespaces). */
const I18N_KEY: Record<Exclude<AuthErrorCode, 'unknown'>, string> = {
  invalidCredentials: 'auth.login.invalidCredentials',
  emailExists: 'auth.signup.emailExists',
  weakPassword: 'validation.passwordRule',
  invalidOldPassword: 'auth.settings.invalidOldPassword',
  mfaInvalidCode: 'auth.mfa.invalidCode',
  mfaExpired: 'auth.mfa.expired',
  mfaLocked: 'auth.mfa.locked',
  mfaResendThrottled: 'auth.mfa.resendThrottled',
  // The authenticator codes live in the reserved `errors.api.*` namespace on purpose: they are
  // rendered on the settings route, which never loads this feature's lazy `auth.*` bundle.
  mfaTooManyCodes: 'errors.api.MFA_TOO_MANY_CODES',
  totpAlreadyEnabled: 'errors.api.TOTP_ALREADY_ENABLED',
  totpSetupRequired: 'errors.api.TOTP_SETUP_REQUIRED',
  totpInvalidCode: 'errors.api.TOTP_INVALID_CODE',
  totpEmailCodeInvalid: 'errors.api.TOTP_EMAIL_CODE_INVALID',
  handoffInvalid: 'auth.login.systemError',
  resetInvalid: 'auth.reset.invalidCode',
  inviteExpired: 'auth.invite.expired',
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** `error.details` from the canonical auth envelope — the server's own state snapshot shipped with
 *  a rejection (the MFA attempts/expiry left after a wrong code). Empty on the legacy v3 body,
 *  which carries no details: callers must degrade, never guess. */
function detailsOf(error: ApiError): Record<string, unknown> {
  return asRecord(asRecord(asRecord(error.payload)?.error)?.details) ?? {}
}

function codeOf(error: ApiError): AuthErrorCode {
  if (error.code && CODE_MAP[error.code]) return CODE_MAP[error.code] as AuthErrorCode
  // Structured fallbacks — code-OR-fields, no English substring matching (contract 06 §3/§14).
  // No HTTP-status qualifier: DRF unique-field errors surface as 400 with `fields.email`.
  if (error.fields.email) return 'emailExists'
  if (error.fields.password) return 'weakPassword'
  // `/auth/register/` (AuthApi.register — signup + Shopify register) is still the legacy v3
  // controller: a taken email rejects as a bare `406 {"error": "Email already exists."}` body,
  // with no machine code and no DRF field map to key off. 406 carries no other meaning on this
  // flow, so the bare status (not the message text) is enough to classify it — confirmed against
  // the live endpoint, which returns nothing else on 406.
  if (error.status === 406) return 'emailExists'
  return 'unknown'
}

export class AuthError extends Error {
  readonly code: AuthErrorCode
  /** i18n key for display: `auth.<screen>.<code>` or `errors.http.<kind>`. */
  readonly i18nKey: string
  /** DRF field errors surfaced to the form (email/password). */
  readonly fields: Record<string, string[]>
  /** Server-authored state shipped with the rejection (`error.details`); `{}` when the server
   *  sent none. Read it defensively — never treat an absent key as a value. */
  readonly details: Record<string, unknown>

  constructor(
    code: AuthErrorCode,
    i18nKey: string,
    fields: Record<string, string[]> = {},
    details: Record<string, unknown> = {},
  ) {
    super(code)
    this.name = 'AuthError'
    this.code = code
    this.i18nKey = i18nKey
    this.fields = fields
    this.details = details
    Object.setPrototypeOf(this, AuthError.prototype)
  }

  /** Maps ApiError.kind/code/fields/details → AuthErrorCode + i18nKey. */
  static from(e: unknown): AuthError {
    if (e instanceof AuthError) return e
    if (ApiError.isApiError(e)) {
      const code = codeOf(e)
      const i18nKey = code === 'unknown' ? e.i18nKey : I18N_KEY[code]
      return new AuthError(code, i18nKey, e.fields, detailsOf(e))
    }
    return new AuthError('unknown', 'errors.http.unknown')
  }
}
